import { inject, Injectable } from '@angular/core';
import { BorderRadius, CartesianScaleOptions, ChartOptions, FontSpec, TitleOptions, TooltipOptions } from 'chart.js';
import { _DeepPartialObject } from 'node_modules/chart.js/dist/types/utils';
import { CssHelperService } from '@app/core/services/css-helper/css-helper.service';

@Injectable({
  providedIn: 'root',
})
export class ChartThemeService {
  private cssHelper = inject(CssHelperService);

  private readonly colors = {
    axisColor: this.cssHelper.readCssVariable('--p-primary-50') || '#E6EEF2',
    axisLabelColor: this.cssHelper.readCssVariable('--p-gray-600') || '#434552',
    titleColor: this.cssHelper.readCssVariable('--p-gray-600') || '#434552',
    legendColor: this.cssHelper.readCssVariable('--p-gray-600') || '#434552',
    tooltipBg: this.cssHelper.readCssVariable('--p-other-opacity-black-70') || 'rgba(0, 0, 0, 0.7)',
    tooltipBorder: this.cssHelper.readCssVariable('--p-other-opacity-black-70') || 'rgba(0, 0, 0, 0.7)',
    tooltipText: this.cssHelper.readCssVariable('--p-surface-0') || '#fff',
    errorLabelColor: this.cssHelper.readCssVariable('--p-other-red-450') || '#DF424F',
    warningLabelColor: this.cssHelper.readCssVariable('--p-orange-550') || '#9B5500',
    standardLabelColor: this.cssHelper.readCssVariable('--p-gray-300') || '#5F6275',
    warningBackdropColor: this.cssHelper.readCssVariable('--p-other-opacity-orange-25') || 'rgba(255, 152, 30, 0.25)',
    hoverLine: this.cssHelper.readCssVariable('--p-primary-900') || '#00344F',
    hoverBarBackground:
      this.cssHelper.readCssVariable('--p-other-opacity-neutral-500-80') || 'rgba(230, 241, 250, 0.8)',
    datasetBackgroundColor: this.cssHelper.readCssVariable('--p-primary-300'),
    datasetColor: this.cssHelper.readCssVariable('--p-primary-500'),
    tickColor: this.cssHelper.readCssVariable('--p-blue-50'),
    transparent: 'rgba(0, 0, 0, 0)',
  };

  public getColors() {
    return this.colors;
  }

  public getBaseFont(): _DeepPartialObject<FontSpec> {
    return {
      size: 11,
      family: 'Manrope',
      weight: 500,
      style: 'normal',
    };
  }

  public getBorderRadius(topLeft: number, topRight: number, bottomLeft: number, bottomRight: number): BorderRadius {
    return { topLeft, topRight, bottomLeft, bottomRight };
  }

  public getAnimation(): ChartOptions['animation'] {
    return { duration: 1500, easing: 'easeOutQuart' };
  }

  public getTitleOptions(): _DeepPartialObject<TitleOptions> {
    return {
      display: true,
      color: this.getColors().titleColor,
      padding: { top: 20, bottom: 10 },
      font: this.getBaseFont(),
    };
  }

  public getScaleTitleOptions(): _DeepPartialObject<CartesianScaleOptions['title']> {
    return {
      display: true,
      align: 'center',
      color: this.getColors().axisLabelColor,
      font: this.getBaseFont(),
      padding: { bottom: 9 },
    };
  }

  public getXScaleOptions(): _DeepPartialObject<CartesianScaleOptions> {
    return {
      display: true,
      ticks: { color: this.getColors().axisLabelColor, font: this.getBaseFont() },
      grid: { display: false, color: this.getColors().axisColor },
    };
  }

  public getYScaleOptions(): _DeepPartialObject<CartesianScaleOptions> {
    return {
      ticks: {
        maxTicksLimit: 6,
        color: this.getColors().axisLabelColor,
        font: this.getBaseFont(),
      },
      grid: { display: true, color: this.getColors().axisColor },
    };
  }

  public getBaseOptions(): ChartOptions {
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: this.getAnimation(),
    };
  }

  public getBasePluginsOptions(): ChartOptions['plugins'] {
    return {
      tooltip: this.getTooltipOptions(),
      title: this.getTitleOptions(),
    };
  }

  getTooltipOptions(): _DeepPartialObject<TooltipOptions> {
    const colors = this.getColors();

    return {
      enabled: true,
      mode: 'x',
      intersect: false,
      backgroundColor: colors.tooltipBg,
      borderColor: colors.tooltipBorder,
      borderWidth: 1,
      titleColor: colors.tooltipText,
      bodyColor: colors.tooltipText,
      titleFont: { ...this.getBaseFont(), weight: 600 },
      bodyFont: this.getBaseFont(),
      padding: 8,
      boxPadding: 3,
    };
  }
}
